import { ServiceIntegrationDescription, ServiceIntegrationModel } from '@ekkogmbh/apisdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createContextHardened } from 'src/Common/Helper/ContextHelper';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';

interface Context {
  selectedIntegration?: ServiceIntegrationModel;
  descriptions: ServiceIntegrationDescription[];
  integrations: ServiceIntegrationModel[];
  /** when loading integrations */
  isFetching: boolean;
  /** when saving or deleting the selected entry */
  isChanging: boolean;
  isFetchingDescriptions: boolean;
  selectIntegration: (index?: number) => void;
  /** overwrites selected or creates new integration entry */
  saveIntegration: (
    name: string,
    coordinate: string,
    configuration: Record<string, unknown>,
    successCallback?: () => void,
  ) => void;
  /** deletes selected integration */
  deleteIntegration: (name: string, coordinate: string) => void;
  fetchIntegrations: () => void;
  fetchDescriptions: () => void;
}

const [DeviceIntegrationsContext, useDeviceIntegrations] = createContextHardened<Context>('Device Integrations');
export default useDeviceIntegrations;

interface Props {
  children: React.ReactNode;
}
export const DeviceIntegrationsProvider = ({ children }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const [isFetchingDescriptions, startFetchingDescriptions] = useRequest<ServiceIntegrationDescription[]>();
  const [isFetching, startFetching] = useRequest<ServiceIntegrationModel[]>();
  const [isChanging, startChanging] = useRequest();

  const [descriptions, setDescriptions] = useState<ServiceIntegrationDescription[]>([]);
  const [integrations, setIntegrations] = useState<ServiceIntegrationModel[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
  const selectedIntegration = useMemo(
    () => (selectedIndex !== undefined ? integrations[selectedIndex] : undefined),
    [selectedIndex, integrations],
  );

  const fetchIntegrations = useCallback(() => {
    setSelectedIndex(undefined);

    startFetching(
      () => api.getServiceIntegrations(),
      (result) => {
        setIntegrations(result ?? []);
      },
    );
  }, [api]);

  const fetchDescriptions = useCallback(() => {
    startFetchingDescriptions(
      () => api.getServiceIntegrationDescriptions(),
      (result) => {
        setDescriptions(result ?? []);
      },
    );
  }, [api]);

  // on mount
  useEffect(() => {
    fetchDescriptions();
    fetchIntegrations();
  }, []);

  const selectIntegration = useCallback(
    (index?: number) => {
      if (index === undefined || index >= integrations.length) {
        setSelectedIndex(undefined);
      } else {
        setSelectedIndex(index);
      }
    },
    [integrations],
  );

  const saveIntegration = useCallback(
    (name: string, coordinate: string, configuration: Record<string, unknown>, successCallback?: () => void) => {
      startChanging(
        () => api.saveServiceIntegration(name, coordinate, configuration, selectedIntegration !== undefined),
        () => {
          if (successCallback) {
            successCallback();
          }
          fetchIntegrations();
        },
      );
    },
    [api, selectedIntegration],
  );

  const deleteIntegration = useCallback((name: string, coordinate: string) => {
    startChanging(
      () => api.deleteServiceIntegration(name, coordinate),
      () => {
        selectIntegration(undefined);
        fetchIntegrations();
      },
    );
  }, []);

  return (
    <DeviceIntegrationsContext.Provider
      value={{
        selectedIntegration,
        descriptions,
        integrations,
        isFetchingDescriptions,
        isFetching,
        isChanging,
        fetchDescriptions,
        fetchIntegrations,
        selectIntegration,
        saveIntegration,
        deleteIntegration,
      }}
    >
      {children}
    </DeviceIntegrationsContext.Provider>
  );
};
